import React, { useContext, useEffect, useRef, useState } from 'react';
import { Col, Row, Button as OLTButton } from '@thd-olt-component-react/core-ui';
import PropTypes from 'prop-types';

import { Button } from '@one-thd/sui-atomic-components';
import ErrorBoundary from '../../contexts/ErrorBoundary';
import { BannerContext, BannerProvider } from '../../contexts/BannerContext';
import PreferenceCard from '../PreferenceCard/PreferenceCard';
import { PHONE_TITLE, phoneTableHeaders } from '../tableData';
import { EPCContext } from '../../contexts/epcContext';
import { OptOutOfAllModal } from './OptOutOfAllModal';
import constants, { NO, PENDING, YES } from '../../constants/constants';
import {
  convertSubsToPrefs,
  getSubscriptions, uncheckCheckBoxes, unsubscribeFromAllSmsSubscriptions,
  updateTextPreferences
} from '../../common/sharedFunctions';
import { addAnalyticEvent } from '../../analytics/common';
import { smsAnalytics } from '../../analytics/save';
import bannerConstants from '../../constants/bannerConstants';
import {
  ConsentConfirmationModal
} from './ConsentConfirmationModal';
import { SignUpModal } from '../SignUpModal/SignUpModal';

export const unsubFromSmsButtonTestId = 'unsubFromSmsButton';

export function Preferences({ isSearchedInitiated }) {
  const {
    openSuccessAlert,
    openErrorAlert,
    hideBanners,
    openPendingAlert,
    closePendingAlert,
    openOptOutOfAllBanner,
    closeOptOutOfAllBanner,
  } = useContext(BannerContext);

  const {
    phone, setPhone, isMobile,
    smsTelemarketingTableData, setSmsTelemarketingTableData, pending,
  } = useContext(EPCContext);

  const [selectedItems, setSelectedItems] = useState([]);
  const [unsubscribeToggle, setUnsubscribeToggle] = useState(false);

  const [togglePromoConsent, setTogglePromoConsent] = useState(false);
  const [toggleProConsent, setToggleProConsent] = useState(false);
  const [toggleConsentConfirmationModal, setConsentConfirmationModal] = useState(false);
  const [toggleSignUp, setSignUp] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [hasOnlyOptIn, setHasOnlyOptIn] = useState(false);
  const [telemarketingCard, setTelemarketingCard] = useState(false);

  const determinePendingText = () => {
    const pro = pending.find((item) => item && item.type === constants.PRO)?.status;
    const promotion = pending.find((item) => item && item.type === constants.PROMOTIONS)?.status;
    if (pro === PENDING && promotion === PENDING) {
      return constants.PRO_PROMO_PENDING;
    }
    if (pro === PENDING) {
      return constants.PRO_PENDING;
    }
    if (promotion === PENDING) {
      return constants.PROMO_PENDING;
    }
    return null;
  };

  useEffect(() => {
    closeOptOutOfAllBanner();
    if (pending.length > 0) {
      openPendingAlert(determinePendingText(), phone, smsTelemarketingTableData);
    } else {
      closePendingAlert();
    }
  }, [pending]);

  useEffect(() => {
    if (smsTelemarketingTableData[PHONE_TITLE].value === NO) {
      setTelemarketingCard(true);
    }
  }, [pending]);

  const handleOptOutCancel = () => {
    setUnsubscribeToggle(false);
  };
  const openUnsubscribeModal = () => {
    hideBanners();
    setUnsubscribeToggle(true);
  };

  const handleSelectedItem = (item, checked) => {
    if (!checked) {
      openOptOutOfAllBanner(true);
    }
  };

  const handleOptOutOfAll = (phoneNumber, res) => {
    hideBanners();
    setTelemarketingCard(true);
    openSuccessAlert(bannerConstants.FCC_OPTOUT, phoneNumber);
    setSmsTelemarketingTableData(uncheckCheckBoxes(smsTelemarketingTableData));
    setPhone(phoneNumber);
    setUnsubscribeToggle(false);
    setSelectedItems([]);
  };

  function containsOptIn(list, table) {
    return list.some((item) => table[item].value === YES);
  }

  function callUnsubFromAll(phoneInputValue) {
    setSmsTelemarketingTableData(uncheckCheckBoxes(smsTelemarketingTableData));
    unsubscribeFromAllSmsSubscriptions(phoneInputValue, (res) => {
      handleOptOutOfAll(phoneInputValue, res);
    }, (err) => {
      openErrorAlert(constants.GENERIC_ERROR);
    });
    setSelectedItems([]);
  }

  const callBackend = (phoneInputValue) => {
    if (selectedItems.length > 0 && containsOptIn(selectedItems, smsTelemarketingTableData)) {
      updateTextPreferences({
        phoneNumber: phoneInputValue,
        subscriptions: selectedItems,
        table: smsTelemarketingTableData,
        callback: (response) => {
          addAnalyticEvent(smsAnalytics(selectedItems));
          if (phone !== phoneInputValue) {
            setPhone(phoneInputValue);
          }
          setConsentConfirmationModal(false);
          setToggleProConsent(false);
          setTogglePromoConsent(false);
          setSelectedItems([]);
          setSmsTelemarketingTableData(convertSubsToPrefs(smsTelemarketingTableData, getSubscriptions(response)));
          if (hasOnlyOptIn) {
            openSuccessAlert(bannerConstants.TEST_MESSAGE_OPT_IN);
          } else {
            openSuccessAlert(bannerConstants.PREFERENCES_UPDATED);
          }
        },
        err: (err) => {
          openErrorAlert(constants.GENERIC_ERROR);
        },
      });
    } else {
      callUnsubFromAll(phoneInputValue);
    }
  };

  const handleConsentConfirmationConfirm = () => {
    callBackend(phoneInput);
  };

  const handleConfirmationStep = (phoneInputValue) => {
    let hasOptOut = selectedItems.some((item) => smsTelemarketingTableData[item].value === NO);

    if (hasOptOut) {
      callUnsubFromAll(phoneInputValue);
    } else {
      let onlyOptIn = false;
      let hasProConsent = false;
      let hasPromoConsent = false;

      selectedItems.forEach((item) => {
        let tableItem = smsTelemarketingTableData[item];
        if (tableItem.value === YES) {
          onlyOptIn = true;
          if (tableItem.type === constants.PRO) {
            hasProConsent = true;
          }
          if (tableItem.type === constants.PROMOTIONS) {
            hasPromoConsent = true;
          }
        }
      });
      setHasOnlyOptIn(onlyOptIn);

      if (hasProConsent || hasPromoConsent) {
        setConsentConfirmationModal(true);
        setToggleProConsent(hasProConsent);
        setTogglePromoConsent(hasPromoConsent);
        setPhoneInput(phoneInputValue);
      } else {
        callBackend(phoneInputValue);
      }
    }
  };

  const handleSaveChanges = () => {
    if (selectedItems.length > 0) {
      hideBanners();
      if (!phone) {
        setSignUp(true);
      } else {
        handleConfirmationStep(phone);
      }
    }
  };

  const handleCancel = () => {
    const tableData = uncheckCheckBoxes(smsTelemarketingTableData, selectedItems);
    setSmsTelemarketingTableData(tableData);
    selectedItems.forEach((item) => {
      if (smsTelemarketingTableData[item].type === constants.PRO) {
        setToggleProConsent(false);
      }
      if (smsTelemarketingTableData[item].type === constants.PROMOTIONS) {
        setTogglePromoConsent(false);
      }
    });
    setSelectedItems([]);
    setConsentConfirmationModal(false);
  };

  const doNotCall = () => {
    return (
      <a href="https://www.homedepot.com/c/Do_Not_Call" style={{ color: 'black' }}><u>Do Not Call Policy</u></a>
    );
  };

  return (
    <div className="component">
      <Row>
        <Row>
          <Col>
            <h2>
              <u style={{ textDecorationColor: '#F96302' }}>
                Marketing Text Messages & Phone Calls
              </u>
            </h2>
          </Col>
        </Row>

        <Row>
          <Col>
            <p>

              We will text you information about how to opt into (and out of) marketing text
              messages
              when you save changes. By opting out of Phone Calls, you will be added to The Home
              Depot’s
              Do Not Call list. The Home Depot may still call you on non-promotional and
              non-marketing
              matters. If wishing to learn more, view the {doNotCall()}. To edit your
              preferences,
              click on the individual preference options below. An opt out of one option will be
              treated
              as an opt out of all preferences below.

            </p>
          </Col>
        </Row>
        <Row />
        {isSearchedInitiated && (
          <Col>

            <Row data-testid="sms-sub-table">
              <PreferenceCard
                isMobile={isMobile}
                headerColumn={phoneTableHeaders}
                tableData={smsTelemarketingTableData}
                name="text"
                setSelectedItems={setSelectedItems}
                setTableData={setSmsTelemarketingTableData}
                handleSelectedItem={handleSelectedItem}
                telemarketingCard={telemarketingCard}
              />
            </Row>

            <Row>
              <Col nopadding xs={6} sm={7} md={7} lg={7} />
              <Col fallback={12} xs={6} sm={5} md={5} lg={5}>
                <Row>
                  <Col>
                    <Button
                      onClick={handleSaveChanges}
                      variant="primary"
                      data-testid="updateTextPreferenceButton"
                      fullWidth
                    >
                      Save Changes
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      data-testid={unsubFromSmsButtonTestId}
                      onClick={openUnsubscribeModal}
                      variant="secondary"
                      fullWidth
                    >
                      Opt Out of All Marketing Texts & Phone Calls
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        )}
        {!isSearchedInitiated && (
          <Row>
            <Col nopadding xs={6} sm={8} md={8} lg={8} />
            <Col fallback={12} xs={6} sm={4} md={4} lg={4}>
              <OLTButton
                data-testid={unsubFromSmsButtonTestId}
                outline
                onClick={openUnsubscribeModal}
              >
                Opt Out of All Marketing Texts & Phone Calls
              </OLTButton>
            </Col>
          </Row>
        )}
        {unsubscribeToggle && (
          <OptOutOfAllModal
            handleClose={handleOptOutCancel}
            handleSuccess={handleOptOutOfAll}
            searchedPhone={phone}
          />
        )}
        {toggleConsentConfirmationModal && (
          <ConsentConfirmationModal
            confirm={handleConsentConfirmationConfirm}
            cancel={handleCancel}
            isProConsent={toggleProConsent}
            isPromotionsConsent={togglePromoConsent}
            isMobile={isMobile}
          />
        )}
        {toggleSignUp && (
          <SignUpModal
            handleSignUpCallBack={handleConfirmationStep}
            type={constants.SMS}
            selectedItems={selectedItems}
            tableData={smsTelemarketingTableData}
            setSignUp={setSignUp}
            setSelectedItems={setSelectedItems}
            setTableData={setSmsTelemarketingTableData}
          />
        )}
      </Row>
    </div>
  );
}

export function SmsTelemarketingPreferences({ isSearchedInitiated }) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences isSearchedInitiated={isSearchedInitiated} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

const propTypes = {
  isSearchedInitiated: PropTypes.bool,
};

const defaultProps = {
  isSearchedInitiated: false,
};

SmsTelemarketingPreferences.propTypes = propTypes;
Preferences.propTypes = propTypes;

SmsTelemarketingPreferences.defaultProps = defaultProps;
Preferences.defaultProps = defaultProps;
